import dayjs from 'dayjs';
import { Timestamp } from 'firebase/firestore';
import { debounce } from 'lodash';
import React from 'react';
import { AiOutlineCheck } from 'react-icons/ai';
import { RiExternalLinkFill } from 'react-icons/ri';
import theme from '../../../styles/theme';
import Budget from '../../../types/TeamCalculator/Budget';
import { ContractLength, Currency, CurrencyLocale } from '../../../types/TeamCalculator/Enums';
import { formatTimestamp } from '../../../utils/date';
import { createOrUpdateItem } from '../../../utils/firebase';
import BudgetForm from '../../ui/BudgetForm';
import Button from '../../ui/Button';
import CardDisplay from '../../ui/CardsDisplay';
import { Input } from '../../ui/Input';
import Select from '../../ui/Input/select';
import { LoadingIcon } from '../../ui/LoadingIcon';
import Modal from '../../ui/Modal';
import { getCurrencyWithFractional } from '../../ui/TeamCalculator/utils';
import { Constants } from '../../ui/TeamCalculator/utils/consts';
import {
  BudgetsWrapper,
  BudgetDescriptionStyled,
  BudgetActionsStyled,
  AcceptedBanner,
  Wrapper,
} from './styles';
import ClientComponent from '../../ui/ClientComponent/ClientComponent';

type AllBudgetsPageTemplateProps = {
  budgets: Budget[];
  allConstants: Constants[] | null;
  loading: boolean;
};

const BudgetDescription: React.FC<{ budget: Budget }> = (props) => {
  const [inputDate, setInputDate] = React.useState('');
  const [adjustmentRate, setAdjustmentRate] = React.useState<number>(
    props.budget.adjustmentRate || 1,
  );
  const [lang, setLang] = React.useState<null | Budget['language']>(props.budget.language || 'en');
  const [currency, setCurrency] = React.useState<keyof typeof Currency>(
    props.budget.currency || 'usd',
  );

  const handleStatus = (status: Budget['status']) => {
    createOrUpdateItem(
      ['budgets'],
      {
        status,
        ...(status === 'accepted' && { acceptedAt: null, declinedAt: null }),
        ...(status === 'declined' && { declinedAt: null, acceptedAt: null }),
      },
      props.budget.id,
      true,
    );
  };

  const handleDateSubmit = () => {
    const { status } = props.budget;
    const date = dayjs(inputDate).toDate();
    createOrUpdateItem(
      ['budgets'],
      {
        ...(status === 'accepted' && { acceptedAt: date, declinedAt: null }),
        ...(status === 'declined' && { acceptedAt: null, declinedAt: date }),
      },
      props.budget.id,
      true,
    );
    setInputDate('');
  };

  const handleUpdateBudget = debounce((data: Budget) => {
    createOrUpdateItem(['budgets'], data, data.id, true);
  }, 250);

  return (
    <>
      <BudgetDescriptionStyled>
        {props.budget.status === 'accepted' && props.budget.acceptedAt && (
          <AcceptedBanner status={props.budget.status}>{`${'Aceito em'} ${formatTimestamp(
            props.budget.acceptedAt,
          )}`}</AcceptedBanner>
        )}
        {props.budget.status === 'declined' && props.budget.declinedAt && (
          <AcceptedBanner status={props.budget.status}>{`${'Declinado em'} ${formatTimestamp(
            props.budget.declinedAt,
          )}`}</AcceptedBanner>
        )}

        <div>
          <div className="description-column">
            <span className="description-item">
              <span className="description-label">Time:</span>
              <span className="description-value">{`${props.budget.members?.length || 0} ${
                props.budget.members?.length === 1 ? 'membro' : 'membros'
              }`}</span>
            </span>
            <span className="description-item">
              <span className="description-label">Tempo de contrato:</span>
              <span className="description-value">
                {props.budget.contractPeriod
                  ? `${ContractLength[props.budget.contractPeriod]} meses`
                  : '-'}
              </span>
            </span>
            <span className="description-item">
              <span className="description-label">Investimento mensal:</span>
              <span className="description-value">
                {props.budget.contractMonthlyInvestment
                  ? getCurrencyWithFractional(
                      CurrencyLocale[currency],
                      currency,
                      props.budget.contractMonthlyInvestment,
                    )
                  : '-'}
              </span>
            </span>
            <span className="description-item">
              <span className="description-label">Valor do contrato:</span>
              <span className="description-value">
                {props.budget.contractTotalInvestment
                  ? getCurrencyWithFractional(
                      CurrencyLocale[currency],
                      currency,
                      props.budget.contractTotalInvestment,
                    )
                  : '-'}
              </span>
            </span>
            <span className="description-item">
              <span className="description-label">Horas mínimas:</span>
              <span className="description-value">
                {props.budget.minimumHours ? `${props.budget.minimumHours}h` : '-'}
              </span>
            </span>
          </div>

          <div className="description-column">
            {props.budget.createdAt && (
              <span className="description-item">
                <span className="description-label">Criado em:</span>
                <span className="description-value">
                  {dayjs((props.budget.createdAt as Timestamp).toDate()).format(
                    'DD/MM/YYYY - HH:mm',
                  )}
                </span>
              </span>
            )}
            {props.budget.updatedAt && (
              <span className="description-item">
                <span className="description-label">Atualizado em:</span>
                <span className="description-value">
                  {dayjs((props.budget.updatedAt as Timestamp).toDate()).format(
                    'DD/MM/YYYY - HH:mm',
                  )}
                </span>
              </span>
            )}

            <span className="description-item">
              <span className="description-label">Constants ID:</span>
              <span className="description-value">
                {props.budget.constantsId ? props.budget.constantsId : '-'}
              </span>
            </span>
            <span className="description-item">
              <span className="description-label">Multiplicador:</span>
              <Input
                style={{ height: 26, marginLeft: 4, maxWidth: 90 }}
                type="number"
                value={adjustmentRate || 1}
                onChange={(e) => {
                  handleUpdateBudget({
                    id: props.budget.id,
                    adjustmentRate: Number(e.target.value) || 1,
                  });
                  setAdjustmentRate(Number(e.target.value) || 1);
                }}
              />
            </span>

            <div className="status-container">
              <span className="description-label">Situação:</span>
              <Select
                onClick={(e) => e.preventDefault()}
                onChange={(e) => handleStatus(e.target.value as Budget['status'])}
                defaultValue={props.budget.status}
              >
                <option value="empty">Vazio</option>
                <option value="calculating">Calculando</option>
                <option value="negotiating">Negociando</option>
                <option value="accepted">Aceito</option>
                <option value="declined">Declinado</option>
              </Select>
            </div>
            {props.budget.status === 'accepted' && !props.budget.acceptedAt && (
              <span className="description-item">
                <span className="description-label">Data de aceitação:</span>
                <span className="description-value">
                  <Input
                    type="date"
                    onChange={(event) => setInputDate(event.target.value)}
                    value={inputDate}
                  />
                  {inputDate && (
                    <button
                      disabled={!inputDate}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDateSubmit();
                      }}
                    >
                      <AiOutlineCheck color="black" />
                    </button>
                  )}
                </span>
              </span>
            )}
            {props.budget.status === 'declined' && !props.budget.declinedAt && (
              <span className="description-item">
                <span className="description-label">Data de rejeição:</span>
                <span className="description-value">
                  <Input
                    type="date"
                    onChange={(event) => setInputDate(event.target.value)}
                    value={inputDate}
                  />
                  {inputDate && (
                    <button
                      disabled={!inputDate}
                      onClick={(e) => {
                        e.preventDefault();
                        handleDateSubmit();
                      }}
                    >
                      <AiOutlineCheck color="white" />
                    </button>
                  )}
                </span>
              </span>
            )}
          </div>
          <div className="description-column">
            {props.budget.responsibleName && (
              <span className="description-item">
                <span className="description-label">Responsável:</span>
                <span className="description-value">{props.budget.responsibleName}</span>
              </span>
            )}

            {props.budget.emailAddresses?.length && (
              <span className="description-item">
                <span className="description-label">Contato:</span>
                <span className="description-value">
                  {props.budget.emailAddresses.map((email, index) => (
                    <a key={email} href={`mailto: ${email}`}>
                      {email}
                      {index < (props.budget.emailAddresses?.length || 0) - 1 && (
                        <span>{', '}</span>
                      )}
                    </a>
                  ))}
                </span>
              </span>
            )}
            <span className="description-item">
              <span className="description-label">Idioma:</span>
              <span className="description-value">
                <Select
                  defaultValue={lang as string}
                  onChange={(e) => {
                    handleUpdateBudget({
                      id: props.budget.id,
                      language: e.target.value as 'pt' | 'en',
                    });
                    setLang(e.target.value as 'pt' | 'en');
                  }}
                >
                  <option value={'en'}>Inglês</option>
                  <option value={'pt'}>Português</option>
                </Select>
              </span>
            </span>
            <span className="description-item">
              <span className="description-label">Moeda:</span>
              <Select
                onClick={(e) => e.preventDefault()}
                onChange={(e) => {
                  setCurrency(e.target.value as keyof typeof Currency);
                  handleUpdateBudget({
                    id: props.budget.id,
                    currency: (e.target.value as Budget['currency']) || 'usd',
                  });
                }}
                defaultValue={props.budget.currency}
              >
                <option value="usd">USD</option>
                <option value="brl">BRL</option>
                <option value="eur">EUR</option>
              </Select>
            </span>
          </div>
        </div>
      </BudgetDescriptionStyled>
    </>
  );
};

const AllBudgetsPageTemplate: React.FC<AllBudgetsPageTemplateProps> = ({
  budgets,
  loading,
  allConstants,
}) => {
  const [sort, setSort] = React.useState('status');
  const [showModal, setShowModal] = React.useState(false);
  const [constantLoading, setConstantLoading] = React.useState(false);

  const cards = React.useMemo(() => {
    return budgets
      .sort((a, b) => {
        // Sorting list by different fields
        // Strings
        if (sort === 'title' || sort === 'status') {
          return a[sort]?.localeCompare(b[sort] || '') || 0;
        }
        // Dates
        if (sort === 'acceptedAt' || sort === 'createdAt') {
          return (
            (a[sort]?.toDate() || new Date()).getTime() -
            (b[sort]?.toDate() || new Date()).getTime()
          );
        }

        if (sort === 'updatedAt') {
          return (
            (b[sort]?.toDate() || new Date('1990-01-01')).getTime() -
            (a[sort]?.toDate() || new Date('1990-01-01')).getTime()
          );
        }
        // String array
        if (sort === 'emailAddresses') {
          if (a.emailAddresses?.length && b.emailAddresses?.length) {
            return a.emailAddresses[0].localeCompare(b.emailAddresses[0]);
          } else {
            if (a.emailAddresses?.length) return -1;
            if (b.emailAddresses?.length) return 1;
          }
        }

        return 0;
      })
      .map((budget) => ({
        id: budget.id,
        title: budget.title || 'Sem título',
        language: budget.language || 'en',
        locked: budget.locked,
        description: <BudgetDescription budget={budget} />,
        navigationIcon: (
          <RiExternalLinkFill style={{ marginLeft: '5px' }} color={theme.colors.blue} />
        ),
      }));
  }, [budgets, sort]);

  const onLeave = React.useCallback(() => {
    localStorage.setItem('allowed-admin', '');
    document.body.scrollTop = 0;
    window.location.reload();
  }, []);

  const handleDuplicateConstant = async () => {
    setConstantLoading(true);

    try {
      const lastConstant = allConstants?.sort(
        (a, b) => Number(b.startsAt?.toMillis()) - Number(a.startsAt?.toMillis()),
      )?.[0];

      if (lastConstant) {
        delete (lastConstant as { id?: string }).id;
        await createOrUpdateItem(['constants'], lastConstant);
        setConstantLoading(false);
      }
    } catch (err) {
      console.log('Error on duplicating constant', err);
      setConstantLoading(false);
    }
  };

  return (
    <Wrapper>
      <BudgetActionsStyled>
        <span>
          <small>Ordenar por</small>
          <Select value={sort} disabled={loading} onChange={(e) => setSort(e.target.value)}>
            <option value={'status'}>Situação</option>
            <option value={'title'}>Nome</option>
            <option value={'emailAddresses'}>Email</option>
            <option value={'acceptedAt'}>Aceitação</option>
            <option value={'createdAt'}>Criação</option>
            <option value={'updatedAt'}>Atualização</option>
          </Select>
        </span>
        <div>
          <Button
            style={{ gap: '6px' }}
            onClick={() => handleDuplicateConstant()}
            disabled={constantLoading}
          >
            {constantLoading && <LoadingIcon style={{ marginTop: '-1px' }} />} Duplicar constant
          </Button>
          <Button onClick={() => setShowModal(true)}>Criar novo budget</Button>
          <Button onClick={() => onLeave()}>Sair</Button>
        </div>
      </BudgetActionsStyled>
      {loading ? (
        <>
          <LoadingIcon /> <span>Loading...</span>
        </>
      ) : (
        <BudgetsWrapper>
          <CardDisplay cards={cards} columns={1} pathLink={'/team-builder?hash='} isBudget />
        </BudgetsWrapper>
      )}
      <Modal visible={showModal} onClose={() => setShowModal(false)}>
        <BudgetForm
          background="white"
          title="Criar novo budget"
          submitText="Criar"
          disableNavigate
          afterSubmit={() => setShowModal(false)}
        />
      </Modal>
    </Wrapper>
  );
};

export default function AllBudgetsPageTemplateClient(props: AllBudgetsPageTemplateProps) {
  return (
    <ClientComponent>
      <AllBudgetsPageTemplate {...props} />
    </ClientComponent>
  );
}
